import { createContext } from 'react';
import type { Hider } from '../../engine/display/occlusion';
import type { ReachInput } from '../../engine/display/gpu/reachBuffers';
import type { Vec3 } from '../../engine/battlefield/types';
import type { Rung } from '../../engine/stealth/ladder';

/**
 * What can hide a figure under this camera frame - the OPAQUE terrain, as a `Hider` (engine/display/occlusion): the
 * svg map's projected faces, or the GPU map's solids (perf overhaul 2b) - handed down to the layers drawn over it, so
 * the work is done once. Null outside the display, or when nothing could hide anyone. Which renderer draws the
 * terrain is the display's business, not this.
 */
export const DisplayOccluderContext = createContext<Hider | null>(null);

/**
 * The TRANSLUCENT terrain - foliage, smoke, mesh: full cover, and a figure behind it half seen (plan design-gaps, T3;
 * the terrain page) - as a `Hider` beside the opaque one. Null outside the display, or when there is none.
 */
export const DisplayVeilContext = createContext<Hider | null>(null);

/**
 * THE REACH HAND-OFF (perf overhaul 2b): while the GPU canvas draws the map's world, the movement overlay draws
 * nothing in the svg and hands its reach here instead, once per change (`publish`, or null to withdraw it). Null
 * while the svg draws it.
 */
export const GpuReachContext = createContext<((reach: GpuReach | null) => void) | null>(null);

/**
 * A hand-off, one per map: the svg side publishes, a layer outside the svg subscribes (`useSyncExternalStore`), and
 * nothing else re-renders - not the scene that holds it. The reach and the sight wedges each have one.
 */
function makeHandoff<T>() {
  let current: T | null = null;
  const listeners = new Set<() => void>();
  return {
    publish: (value: T | null) => {
      if (value === current) return;
      current = value;
      for (const l of listeners) l();
    },
    subscribe: (l: () => void) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    get: () => current,
  };
}

/** The reach's hand-off: the movement overlay publishes, the GPU map's reach layer subscribes. */
export const makeReachStore = () => makeHandoff<GpuReach>();

export type ReachStore = ReturnType<typeof makeReachStore>;

/** What the overlay hands the GPU map: the reach as reachBuffers takes it, and the cue's ripple (a new one restarts). */
export type GpuReach = ReachInput & { ripple?: 'a' | 'b' };

/**
 * THE SIGHT WEDGES' HAND-OFF (2026-11-01): while the canvas draws the map, the wedges leave the svg - any change
 * inside it repaints the whole map - for a CSS plane over the canvas, whose turning the browser runs on the
 * compositor (WedgePlane). SightWedges publishes here, once per change. Null while the svg draws them.
 */
export const GpuWedgesContext = createContext<((wedges: GpuWedges | null) => void) | null>(null);

/** The sight wedges' hand-off: SightWedges publishes, the stage's wedge plane subscribes. */
export const makeWedgeStore = () => makeHandoff<GpuWedges>();

export type WedgeStore = ReturnType<typeof makeWedgeStore>;

/**
 * What the wedges hand the plane: the rung (the sweep's tempo), whether this screen holds still, each watcher's
 * foot, its Dark Sight, its seeded bearing at rest and - turned to a noise it heard - the bearing it stares along;
 * and the party leader's foot, for their own sweep.
 */
export interface GpuWedges {
  rung: Rung;
  still: boolean;
  watchers: readonly { id: string; x: number; z: number; darkSight: boolean; base: number; heard: number | null }[];
  leader: { x: number; z: number } | null;
}

/**
 * The display's cut rises played (hooks/useDisplayCut; 0 off the display and before any): the layers over the set
 * play a cut's cast change on it (Z3).
 */
export const DisplayRiseContext = createContext(0);

/** What the display scene needs of the action (plan map-display, C): where it fades from, and whom the x-ray keeps. */
export interface DisplaySceneAction {
  focusPoint: Vec3 | null;
  xrayFigures: readonly XrayFigure[] | null;
  /** The ambient marks the viewer has on (plan design-gaps, T4); null off the display. */
  ambient?: AmbientMarks | null;
}

/** Where a figure stands, as the ambient marks read it. */
type Standing = { x: number; z: number; elevation: number };

/**
 * The ambient marks (T4: useDisplayAction `ambientOf`): the actor and target the sight wedge runs between, and whether
 * the scanlines and the vignette are on.
 */
export interface AmbientMarks {
  wedge: { from: Standing; toward: Standing } | null;
  scanlines: boolean;
  vignette: boolean;
}

/** A figure the x-ray keeps seen: where it stands and how tall it is (a marker carries all of it). */
export interface XrayFigure {
  x: number;
  z: number;
  elevation: number;
  height?: number;
  eyeHeight: number;
}

/**
 * The action, handed to the display scene by the panel (useDisplayAction):
 * display-only inputs ride here rather than on the props both renderers take.
 */
export const DisplayActionContext = createContext<DisplaySceneAction | null>(null);
